/**
 * WS-F §3 rule 1 — **absence has five treatments and they are never one blank.**
 *
 * `empty` (asked, found nothing) · `errorstate` (the query failed) · `notcollected` (cannot
 * be measured here) · `unimplemented` (we could not ask at all) · `forbidden` (asking is not
 * yours to do). R1 exists in the program's history because the first three WERE one silent
 * blank, and #879 — *"observability errors rendered nowhere"* — is the open issue that names
 * it on the logs screen specifically.
 *
 * ## ⛔ THIS IS THE LIBRARY, and it took until 2026-08-07 to get here
 *
 * ⚠️ This file lived in `src/panes/` until the console design reset — beside the pages that consume
 * it, imported by every one of them on a *relative* path so it read as pane-local. It is the
 * **most-adopted design primitive in the product** (480 uses across 33 files), and two consequences
 * of the wrong home are on the record:
 *
 *  1. Page authors could not tell it was shared, so it was extended by copying rather than by
 *     adding an arm (`NotAuthorizedPanel`, `ZeroAccessPanel`, and the now-deleted `PlannedSurface`).
 *  2. **`ForbiddenState` was written as a fifth arm and never enumerated in {@link ABSENCE_KINDS}**,
 *     so it emitted `data-absence="errorstate"` — indistinguishable, to every machine, from a 500.
 *     ⛔ **A vocabulary whose own enumeration is incomplete cannot be gated**, and that is precisely
 *     how the fifth arm went three months claiming to be the second.
 *
 * ⇒ A page imports this from `shell/`, and a new absence is added HERE, as an arm, with a row in the
 * {@link ABSENCE_KINDS} table stating what happened. A new component beside this file is a fork.
 *
 * ## ⛔ Why each one carries `data-absence`, and why that attribute is load-bearing
 *
 * WS-F §6 assertion 4 requires a failed log query to be **distinguishable IN THE DOM** from
 * an empty one, with "collapse both to one blank" as the mutation that must break it. Two
 * components that render different *prose* satisfy a human reader and nothing else: a test
 * asserting on copy breaks the moment someone rewords it, so the assertion would be quietly
 * retired by an unrelated edit. `data-absence` is a stable, machine-checkable contract —
 * and `absence.test.tsx` asserts the five values are pairwise distinct, so collapsing
 * any two reds immediately.
 *
 * ⚠️ They are NOT five variants of one component behind a `kind` prop. Five call sites
 * that each pick a string are five chances to pick the wrong one silently; five named
 * components mean the choice is visible in the diff and a reviewer can see which absence a
 * panel claims.
 */
import {
  AlertTriangle,
  CircleSlash,
  HelpCircle,
  RefreshCw,
  Unplug,
} from 'lucide-react';
/*
 * The one sanctioned source of a state colour, and the reason `ErrorState.tone` takes a verdict
 * rather than a class string. A `tone` prop that took Tailwind classes would put a colour decision
 * back at the call site, which is the drift `data-tone.tsx`'s ratchet exists to stop.
 */
import { dataToneClass } from './data-tone';
import { UNTRUSTED_BLOCK_MAX_CHARS, displayUntrusted } from '../platform/untrusted-text';

/**
 * The renderings, as a closed union. Consumed by the test that pins them apart.
 *
 * ## ⭐ The whole vocabulary, one row per distinct thing that happened
 *
 * | kind | what happened | who resolves it |
 * |---|---|---|
 * | `empty` | we asked, and the answer is genuinely nothing | nobody — this is the answer |
 * | `errorstate` | we asked, and it failed | retry, or whoever owns the producer |
 * | `notcollected` | we asked, and **nothing is exporting** the thing | whoever ships the producer |
 * | `unimplemented` | **we could not ask at all** — no code path exists | **us**, and it has an issue |
 * | **`forbidden`** | **asking is not yours to do** — the grant is missing | **a person**, by granting it |
 * | **`withheld`** | **we have it and are deliberately not rendering it, OR it is deliberately not available to render, by producer contract** | **a PRODUCER CONTRACT** |
 *
 * ⭐ `unimplemented` was the fourth member, added 2026-08-06.
 *
 * ## ⭐⭐ `withheld` — the SIXTH member, added 2026-08-07. Lead ruling ACK-b4-2.
 *
 * ⚠️ **It is the only member about a decision WE made rather than about the world.** The other five
 * describe what happened when we asked; this one describes what we chose to do with the answer.
 *
 * **The case it was built for:** `PlatformSettingsPane` renders the live cluster EnvironmentConfig. The
 * producer projected `entries[]` as `{ key, value, group }` with **no indication of which keys are
 * secret** — and the object measurably carries **credentials**: three NATS operator/account JWTs, a
 * `.creds` blob holding a user nkey **seed**, and a base64 mkcert root CA (`dpNatsOperatorJwt`,
 * `dpNatsSysAccountJwt`, `dpNatsZionAccountJwt`, `dpNatsOperatorUserCreds`, `mkcertRootCaB64`).
 * ⇒ every value was withheld, unconditionally, pending a masking contract (**#1369**).
 *
 * ## ⭐⭐ THE DEFINITION WIDENED ONE CLAUSE WHEN #1369 LANDED (R-11 / R-49 / RI-7)
 *
 * The original clause was *"we have it and are deliberately not rendering it"* — **a claim about the
 * CONSUMER'S choice.** #1369 moved the decision to the producer: `site-admin-api` now declares
 * `secret: true` per key and sends `value: null`, so the console **does not have the value to
 * withhold**. Read literally, the original clause had stopped being true of its own founding case.
 *
 * ⛔ **The wrong two fixes, and why:**
 *  · Retire `withheld` and use `notcollected` — ⛔ FALSE, and it is the tempting one. `notcollected`
 *    means *nothing is exporting the thing* and dispatches the reader to **go build a producer**. The
 *    producer exists, ran, and deliberately sent nothing. Sending someone to build what already works
 *    is the same class of wasted errand as `forbidden`'s impossible grant request.
 *  · Add a seventh member for *"the producer withheld it"* — ⛔ the fact is the same fact and the owner
 *    is the same owner (a producer contract). Two kinds for one fact is how a vocabulary stops being
 *    gateable, which this file's own `forbidden` history records at length.
 *
 * ⇒ ⭐ **one clause, not a new member:** *…**OR it is deliberately not available to render, by producer
 * contract***. Both halves share the thing that decides the rendering — **a deliberate decision, not a
 * failure, and no grant unlocks it** — which is the only property the kind needs to carry.
 *
 * ⛔ **THE GREP-ABLE SENTENCE, R-49, verbatim in every adopter:**
 *
 *     Masked by contract. The server never sends the secret value.
 *
 * Use those exact words. R-49's point is ONE grep-able string rather than two paraphrases, because the
 * env-config settings copy (WS-4) and the IdP-config `FormField 'secret'` helper text (WS-6a) are
 * **different interactions** that must not diverge in how they describe the property they share.
 * ⚠️ They stay different where they genuinely differ: env-config secrets are **display-only** (set
 * elsewhere, no edit affordance), while the IdP field IS editable with omit-to-leave-unchanged
 * sentinel semantics. The shared sentence covers the masking; the sentinel is documented on top of it.
 *
 * ⚠️ The `withheld` kind renders at cell scale only, through {@link CellAbsence} here and its WARD
 * twin in `ward/absence.tsx`. The block arm left with its last adopter, Platform settings, which
 * states the policy beside its withheld count (plan 2026-10-21-console-ward-platform-settings, I7).
 *
 * ⛔ **NONE of the other five could carry that claim, and four of the five are near-misses — which is
 * exactly why a sixth member was cheaper than a force-fit:**
 *
 *  · `empty` — ⛔ FALSE. The value exists; we are choosing not to show it.
 *  · `errorstate` — ⛔ FALSE. Nothing failed.
 *  · `notcollected` — ⛔ FALSE. The producer *did* export it; it is in the response.
 *  · `unimplemented` — ⛔ FALSE on the one axis that matters: it claims *"we could not ask at all"*, and
 *    we asked and received. ⚠️ Right owner and right expiry semantics, wrong fact — the most tempting
 *    wrong answer.
 *  · ⛔⛔ `forbidden` — **FALSE AND HARMFUL.** It says the grant is missing and a person can grant it.
 *    **No grant unlocks a withheld value** — it is withheld from every principal including a full
 *    `platform_admin` — so that label would send an operator to ask for access **that cannot exist**.
 *    A near-miss that dispatches someone on an impossible errand is worse than an obviously-wrong one.
 *
 * ⭐ **And the precedent for expanding rather than folding is in this file's own history:**
 * {@link ForbiddenState} spent three months emitting `data-absence="errorstate"` *because there was no
 * fifth value to pass*, and the verdict above is ⛔ *"a vocabulary whose own enumeration is incomplete
 * cannot be gated."* Repeating that with a **security** decision as the thing mislabelled is not a
 * trade worth one avoided library edit. This member migrates **zero** existing callers.
 *
 * ⚠️ **`withheld` is NOT `notstated`.** Two ports specified a `notstated` member for a *null field on a
 * present row* (`AuditPane`'s null outcome, `PlatformSettingsPane`'s null deploy annotations). That is
 * `notcollected` with `label="not stated"` — *the producer sent nothing for it* — and both screens use
 * exactly that. ⛔ Do not add a seventh member for it: **the two are different facts** (our decision vs
 * the producer's silence) and they must not share a kind, which is why `withheld` exists and
 * `notstated` does not.
 *
 * ## ⛔⛔ `forbidden` — the fifth member, and it was ALREADY BEING RENDERED for three months
 *
 * ⚠️ **This is not a new arm. It is an arm that existed and was not enumerated.**
 * {@link ForbiddenState} has shipped since WS-F, has ~30 call sites, and until 2026-08-07 it passed
 * `kind="errorstate"` to {@link Frame} — because there was no fifth value to pass. So the DOM said
 * *"the query failed"* on every permission boundary in the product.
 *
 * ⛔ **That is the exact defect `data-absence` exists to prevent, committed by the file that owns
 * it.** A `403` and a `500` were byte-identical to every machine reading the console: the panes
 * distinguished them only by hand-picked `data-testid` strings (`*-forbidden` vs `*-errorstate`),
 * i.e. by a per-call-site convention with no definition and nothing checking it. `errorstate`'s own
 * docblock says the status is shown *"because a 403 means something categorically different from a
 * 500 to the person reading"* — and then the kind said they were the same thing.
 *
 * ⛔ **The two are not interchangeable, and the right-hand column above is why:** an `errorstate`
 * is resolved by *time* (retry, or a fix lands), a `forbidden` is resolved by *a person* (someone
 * grants you the permission). An operator who triages a permission boundary as an outage files a bug
 * against a working system; one who triages an outage as a permission boundary goes and asks for
 * access that would not have helped.
 *
 * ⚠️ It also unblocks the in-cell case: {@link CellAbsence} takes an `AbsenceKind`, so before this
 * there was **no way to render a denial in a `<td>`** — which is why `TenantsPane`'s spend column
 * renders a billing 403 as *"not collected"*, a claim about the producer we have no evidence for.
 * That call site is NOT fixed here (it also needs a permission gate); this only makes it expressible.
 *
 * ⛔ **Rendering a new absence locally in one pane is always the worse option**, and the reason is
 * the reason this file exists: the NEXT surface to meet the same response has no vocabulary for it
 * and collapses it into a neighbouring arm — recreating, somewhere else, the exact defect the
 * addition fixes. A file whose purpose is keeping absences distinct cannot refuse a distinct absence.
 *
 * ⚠️ Adding a member is NOT the same as collapsing the family into variants behind a `kind` prop.
 * That remains forbidden for the reason in this module's header: five call sites each picking a
 * string are five chances to pick the wrong one silently, whereas five named components make the
 * choice visible in the diff.
 */
export const ABSENCE_KINDS = [
  'empty',
  'errorstate',
  'notcollected',
  'unimplemented',
  'forbidden',
  'withheld',
] as const;
export type AbsenceKind = (typeof ABSENCE_KINDS)[number];

/**
 * The three verdicts a write failure uses, and the reason the set is smaller than
 * {@link DataVerdict}.
 *
 * A failed write is never `good`, and `none` is the absence of a verdict. Neither is expressible
 * about a refusal. Naming the subset here means an `ErrorState` caller cannot pass a verdict the
 * write-failure vocabulary has no meaning for, and it keeps {@link dataToneClass} the one source of
 * the colour (R-043, R-058, D3-20).
 *
 * | verdict | the write failure it grades |
 * |---|---|
 * | `notable` | the pin moved under the dialog — a race, nothing was applied |
 * | `caution` | the door refused the version — a state to fix, not a fault |
 * | `bad` | the change failed — a transport fault or a server error |
 */
export type WriteFailureTone = 'notable' | 'caution' | 'bad';

function Frame({
  kind,
  testId,
  icon,
  title,
  children,
  tone,
  assertive = false,
  verdict,
  details,
}: {
  readonly kind: AbsenceKind;
  readonly testId: string;
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly children?: React.ReactNode;
  readonly tone: string;
  /**
   * `role="alert"` and `aria-live="assertive"` instead of today's pair.
   *
   * Default `false`, which is the shipped render. A write failure interrupts: the operator pressed
   * a button and is waiting on its outcome. An absence is information that arrived where content
   * was expected. Only `ErrorState` passes it, and only for a write (R-061).
   */
  readonly assertive?: boolean;
  /**
   * Mirrored to `data-verdict-tone`, the package's machine-readable verdict channel.
   *
   * Default: no attribute, as today. The colour is never the only carrier of the verdict. This
   * attribute and the title sentence carry it too, and those are what a test and a monochrome
   * screen read.
   */
  readonly verdict?: WriteFailureTone;
  /**
   * Flow content below the phrasing `<span>`, in a `<div>` of its own, so a list can live there.
   *
   * It is not merged into `children`. `children` renders inside a phrasing `<span>` with the
   * package's `max-w-[100ch]` measure, and a `<ul>` inside a `<span>` is invalid flow content that
   * the browser reparents. Prose does not belong here either: this slot is unmeasured, so a
   * sentence in it escapes `prose-measure.test.ts`. Sentences go in `title` and `children`
   * (R-065).
   */
  readonly details?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      data-absence={kind}
      data-testid={testId}
      data-verdict-tone={verdict}
      /*
       * `role="status"` + `aria-live="polite"`: all five of these replace content a
       * screen-reader user was waiting for. Announced politely rather than assertively —
       * an absence is information, not an interruption.
       *
       * `assertive` flips both, together. They are one decision: `role="alert"` with
       * `aria-live="polite"` is a contradiction that screen readers resolve differently from
       * each other, so the two never move apart.
       */
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      className={`flex flex-col items-start gap-1.5 rounded-lg border px-4 py-3 text-xs ${tone}`}
    >
      <span className="flex items-center gap-2 font-semibold text-primary">
        {icon}
        {title}
      </span>
      {/*
        ⭐⭐ **`max-w-[100ch]` — THE MEASURE FOR EVERY ABSENCE IN THE PACKAGE, IN ONE LINE.**
        (Found by `panes-ops` sweeping rendered text; the gate was blind to it twice over.)

        This one `<span>` renders the caller's sentence for **all five** absence arms —
        `EmptyState.hint`, `NotCollectedState.reason`, `UnimplementedState` (reason + remedy +
        tracking) and `ForbiddenState`. ⇒ **one measure here bounds every pane that renders an
        absence**, which on this console is most of them.

        ## ⛔ WHY `prose-measure.test.ts` COULD NOT SEE THIS — both of its blindnesses at once

        1. **Prop prose.** The sentence arrives as `{children}`, so the literal-text scan reads
           **zero characters** and the element is exempt no matter how long the caller's string is.
        2. **Not a `<p>`.** It is a `<span>`, so the original `<p>`-only scan never looked at it.

        ⚠️ **And the page cap was hiding the consequence.** While `PaneFrame` capped the page at
        1400px this rendered at ~138ch — already over the limit, invisibly. With the cap gone it was
        free to reach the viewport: the browser sweep measured **257ch on `platform/capacity` and
        213ch on `releases/apps`**, both of which are this component.

        ⛔ **DO NOT move this to the call sites.** Same shape as `META_LABEL_CLASS`: one shared
        definition behind many rendered sites, where fixing the definition fixes them all and
        sweeping the callers fixes none of them properly — and leaves the next absence arm unmeasured.

        ⚠️ `ch`, never `px`. `ch` is font-relative, so this holds the same LINE LENGTH at every rung
        of the type ladder; a `px` measure silently changes its character count on every re-scale
        (measured: `max-w-3xl` is ~139ch at 11px and ~110ch at 14px, which is how it passed a
        presence-check while permitting the exact lines this gate exists to stop).
      */}
      {children !== undefined && (
        <span className="max-w-[100ch] text-secondary">{children}</span>
      )}
      {/*
        The flow slot is a `<div>`, deliberately, and that is the reason this prop exists at all.

        The `<span>` above is phrasing content. `AppRolloutPane`'s refusal renders the door's
        blocks through the pane's own `<ul>` renderer, and a `<ul>` inside a `<span>` is invalid:
        the browser closes the span and reparents the list, so the measure above stops applying to
        the sentence it was written for. So this is a sibling `<div>` rather than a wider
        `children`.

        There is no measure here, and that is a statement about what may go in it. A measure would
        invite prose, and prose in this slot is invisible to `prose-measure.test.ts` §3, whose scan
        reads literal text at the call site while the call site passes an element. The two measured
        spans are `title` and `children`. This slot carries structure, a list and a line per
        remedy, whose own renderer owns its measure.
      */}
      {details !== undefined && <div className="w-full">{details}</div>}
    </div>
  );
}

/**
 * **`empty`** — the query ran, the answer is genuinely nothing.
 *
 * ⛔ Never used for a failure. `subject` names what was looked for, because "No results" on
 * its own leaves the reader unsure whether the filter or the fleet is empty.
 */
export function EmptyState({
  subject,
  hint,
  testId = 'absence-empty',
}: {
  readonly subject: string;
  readonly hint?: string;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <Frame
      kind="empty"
      testId={testId}
      tone="border-border-subtle bg-surface-muted"
      icon={<CircleSlash aria-hidden="true" className="h-3.5 w-3.5 text-muted" />}
      title={`No ${subject}`}
    >
      {hint}
    </Frame>
  );
}

/**
 * The click handler of Try again in both `ErrorState` sets.
 *
 * A retry swaps the error state for a loading line, so the button leaves the page. When the button
 * holds focus, the handler first moves focus to the nearest {@link RETRY_FOCUS_TARGET} ancestor
 * that has a `tabindex`, with `preventScroll`. The loop skips a match with no `tabindex`, such as a
 * plain tab panel, and tries the next match out. These containers stay mounted through the reload
 * and a second failure. A wrapper with a `tabindex` that unmounts with the error state is never the
 * target. A button without focus, such as one a pointer clicked in a browser that does not focus
 * buttons, moves no focus. With no focusable match, no focus moves, and focus falls to the body
 * when the button unmounts. `onRetry` runs in every case, after the move.
 */
export function retryKeepingFocus(button: HTMLElement, onRetry: () => void): void {
  if (document.activeElement === button) {
    for (let node = button.parentElement; node !== null; node = node.parentElement) {
      if (node.matches(RETRY_FOCUS_TARGET) && node.hasAttribute('tabindex')) {
        node.focus({ preventScroll: true });
        break;
      }
    }
  }
  onRetry();
}

/**
 * The containers that outlive a retry and hold the focus while it runs: a pane scroll region, a
 * dialog's content and a tab panel. {@link retryKeepingFocus} moves the focus to the nearest one.
 */
export const RETRY_FOCUS_TARGET = '[data-pane-region], [role="dialog"], [role="tabpanel"]';

/**
 * How much of the screen a {@link ForbiddenState} replaces. `panel` (the default) is one panel
 * among others, so the notice says the rest of the screen is unaffected. `page` is the whole page
 * body, so the notice names the page and says nothing about a rest.
 */
export type ForbiddenExtent = 'panel' | 'page';

/**
 * **`errorstate`** — the query FAILED. This is #879's rendering.
 *
 * ⛔ Carries the server's own message, not a generic "something went wrong": the sentence
 * the server sent is the only thing on screen capable of telling the operator what to fix,
 * and `apiErrorMessage` exists precisely because `ApiError.message` throws it away.
 *
 * ⚠️ `onRetry` is optional and renders a button, never a timer. §6 assertion 10.
 *
 * ## It is also the write-failure surface, and that is a package ruling (F-7 / D86(d))
 *
 * `AppComputePane` renders a failed write through this component rather than a hand-rolled
 * banner, and `AppRolloutPane`'s pin, promote and demote refusals joined it under R-041 and
 * R-058. A write failure differs from a read failure in four ways, and each one is an optional
 * prop whose default is the read-failure render every existing adopter already gets:
 *
 * | prop | a read failure | a write failure |
 * |---|---|---|
 * | `title` | `Couldn't load ${subject}` | the sentence naming what the server refused |
 * | `tone` | the critical pair | {@link WriteFailureTone}; a refusal is not always a fault |
 * | `assertive` | `role="status"`, polite | `role="alert"`, assertive; the operator is waiting |
 * | `details` | none | structure below the sentence: the door's blocks, one remedy per kind |
 *
 * Every existing adopter renders byte-identical. The four defaults reproduce the shipped markup
 * exactly, down to the two literal state classes below, and `absence.test.tsx` pins each of
 * `Frame`'s five callers against its literal HTML, so a later tidy-up of these defaults reds.
 *
 * In particular `tone` does not default to `dataToneClass('bad', 'panel')`. That string carries a
 * `text-primary` this component has never rendered, so routing the default through the vocabulary
 * would have restyled every adopter at once.
 */
export interface ErrorStateProps {
  /**
   * What failed to load, interpolated into the default title.
   *
   * Optional only because `title` replaces the sentence it feeds. A read failure passes
   * `subject`; a write failure passes `title`. Passing neither renders a title naming nothing,
   * which the component's parameter type refuses (R-071).
   */
  readonly subject?: string;
  /** The measured body sentence: the server's own text on a read, a fixed sentence on a write. */
  readonly message: string;
  /** HTTP status, or `null` when the failure never reached a response. */
  readonly status: number | null;
  readonly onRetry?: () => void;
  readonly testId?: string;
  /** The heading. Default: `Couldn't load ${subject}`, today's text for every existing adopter. */
  readonly title?: string;
  /** Recolours the glyph and the frame through `dataToneClass`, and sets `data-verdict-tone`. */
  readonly tone?: WriteFailureTone;
  /** `role="alert"` and `aria-live="assertive"`. Default `false`: today's polite status region. */
  readonly assertive?: boolean;
  /** Flow content below the measured sentence. Structure only, no prose. See `Frame.details`. */
  readonly details?: React.ReactNode;
}

/**
 * The parameter is narrower than {@link ErrorStateProps}, and R-071 is why.
 *
 * Both `subject` and `title` are optional on the interface, because either one alone is a complete
 * call. Neither is not. The default heading is `Couldn't load ${subject}`, so a call passing
 * neither renders the literal words "Couldn't load undefined" at the top of a failure notice.
 *
 * That state is unreachable from every call site in the tree, and no honest test can cover it: a
 * test producing it would assert a render the contract forbids. So it could not be closed with an
 * arm, and a JSDoc sentence saying "pass one of the two" is a comment a future caller does not
 * read. The intersection makes it a compile error instead, at no runtime cost. There is no branch
 * here, and the emitted JavaScript is unchanged.
 *
 * The exported {@link ErrorStateProps} keeps both props optional deliberately. It is the shape a
 * wrapper spreads and a variable is annotated with. Narrowing the interface itself would break
 * every such holder, for a constraint that only matters at the call.
 *
 * `absence.test.tsx` pins this with a `@ts-expect-error` arm, which cannot rot into a comment: if
 * the illegal call ever compiles, the directive itself becomes the error.
 */
export function ErrorState({
  subject,
  message,
  status,
  onRetry,
  testId = 'absence-errorstate',
  title,
  tone,
  assertive,
  details,
}: ErrorStateProps &
  ({ readonly subject: string } | { readonly title: string })): React.JSX.Element {
  return (
    <Frame
      kind="errorstate"
      testId={testId}
      /*
       * The two literals are the shipped default and are not a ratchet regression. They are the
       * bytes every existing adopter renders today, and `dataToneClass('bad', 'panel')` is not
       * equal to them because it adds `text-primary`. A `tone` from the caller routes through the
       * vocabulary instead.
       *
       * A toned frame is the neutral fill with the verdict on its edge at full strength, not the
       * `panel` row. Measured against the tokens: a write-failure notice sits inside a dialog that
       * is itself a `caution` panel, and `panel`'s `/40` border composited over that dialog is
       * 1.70:1 in light, under WCAG 1.4.11's 3:1 for a component boundary. The `edge` row's
       * full-strength colour is 4.34:1 light and 10.75:1 dark for `caution`, and clears 3:1 for
       * all three verdicts in both polarities. The neutral border alone would be 2.90:1 light,
       * which is why the verdict colour stays on the edge rather than moving to `border-subtle`.
       *
       * One background utility and one border-colour utility, deliberately. Two of either are
       * resolved by stylesheet order rather than by the order they appear here.
       */
      tone={
        tone === undefined
          ? 'border-state-critical/40 bg-state-critical/5'
          : `bg-surface-muted ${dataToneClass(tone, 'edge')}`
      }
      verdict={tone}
      assertive={assertive}
      details={details}
      icon={
        <AlertTriangle
          aria-hidden="true"
          className={`h-3.5 w-3.5 ${tone === undefined ? 'text-state-critical' : dataToneClass(tone, 'text')}`}
        />
      }
      title={title ?? `Couldn't load ${subject}`}
    >
      <span className="flex flex-col items-start gap-2">
        <span>
          {/* `displayUntrusted` escapes bidi and control code points and redacts secret shapes. Most
              server error text has no server redactor (#2219), so this is its only control. */}
          {displayUntrusted(message, { maxChars: UNTRUSTED_BLOCK_MAX_CHARS })}
          {/* The status is shown because it is the first thing anyone asks for, and a 403
              means something categorically different from a 500 to the person reading. */}
          {status !== null && (
            <span data-testid={`${testId}-status`} className="ml-1 font-mono text-muted">
              (HTTP {status})
            </span>
          )}
        </span>
        {onRetry !== undefined && (
          <button
            type="button"
            onClick={(event) => retryKeepingFocus(event.currentTarget, onRetry)}
            data-testid={`${testId}-retry`}
            className="inline-flex items-center gap-1.5 rounded border border-border-subtle px-2 py-1 font-medium text-secondary hover:border-border-strong hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
          >
            <RefreshCw aria-hidden="true" className="h-3 w-3" />
            Try again
          </button>
        )}
      </span>
    </Frame>
  );
}

/**
 * **`notcollected`** — nothing has measured this, so there is no answer to give.
 *
 * ⛔ A PRODUCER statement, never a transport one. It is what `podAttributionView`'s
 * `{state:'notcollected', reason}` arm means (#971 / R4-12: the DP has not been released
 * with the attribution labels yet) and what `AppFleetSummary.oldestReportAt === null`
 * means. Rendering either as blank tells an app owner their pod is unattributed when in
 * truth nothing has looked.
 *
 * `reason` is REQUIRED, and it is the whole value of this state: an unexplained
 * "not collected" is indistinguishable from a bug to the person reading it.
 */
export function NotCollectedState({
  subject,
  reason,
  testId = 'absence-notcollected',
}: {
  readonly subject: string;
  readonly reason: string;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <Frame
      kind="notcollected"
      testId={testId}
      tone="border-state-warning/40 bg-state-warning/5"
      icon={<HelpCircle aria-hidden="true" className="h-3.5 w-3.5 text-state-warning" />}
      title={`${subject} not collected`}
    >
      {reason}
    </Frame>
  );
}

/**
 * **`unimplemented`** — the question could not be ASKED. No code path exists to answer it.
 *
 * ## ⛔ NOT {@link NotCollectedState}, AND THE DIFFERENCE IS THE POINT
 *
 * `notcollected` means *we asked and nothing is exporting this*. This means *there is nothing here
 * that can ask*. Rendering the former in this situation states a specific fact about the remote
 * system that we have no evidence for — the same class of defect as rendering an absence as a zero,
 * one level up.
 *
 * The concrete case it was built for: a data plane reporting telemetry to its own cloud-native
 * monitoring stack, for which the control plane has no query adapter (#1269). Every chart came back
 * empty and the UI announced *"either nothing emits this metric or the data plane was idle"* — two
 * specific claims, both false, both actionable, on a screen an operator uses during an incident.
 *
 * ## Why it carries a remedy and a tracking issue when the others do not
 *
 * `empty`, `errorstate` and `notcollected` describe a state of the world, and `forbidden` describes
 * a state of your grants. This one describes a **gap in our own software**,
 * so it always has an owner and an expiry date. ⛔ `remedy` and `trackingIssue` are REQUIRED, not
 * optional: *"unavailable, good luck"* is not a contract, and an unimplemented notice with no issue
 * behind it is indistinguishable from one nobody intends to fix.
 *
 * ⚠️ Tone is `state-info`, deliberately **not** the `state-warning` of `notcollected`. Nothing is
 * wrong with this data plane; the gap is on our side, and colouring it as a warning would point the
 * operator at their own system.
 *
 * ## ⛔ THIS COMPONENT'S TITLE HARD-CODES THE DATA PLANE — so it has a NARROW domain
 *
 * ⛔ It may only be used where **a data plane genuinely IS the subject**. Rendered anywhere else it
 * manufactures a specific claim nobody made — a control-plane-scoped gap would read *"X is not
 * supported for this data plane yet"* when there is no data plane in scope at all — and fabricated
 * specific claims are the exact defect class this whole file exists to stop. ⭐ A caller-supplied
 * title would lift the restriction and is tracked as a follow-up.
 *
 * ## ⚠️ HISTORY, so the deleted sibling is not re-created by someone reading only this file
 *
 * A page-REGION twin lived in `panes/PlannedSurface.tsx` — `<section aria-labelledby>` + a real `<h2>`
 * + a *"Planned — not built"* badge — answering *where will this go* rather than replacing a panel
 * body, and deliberately NOT a live region (a permanent page marker is on screen before the reader
 * arrives and still there when they leave, so {@link Frame}'s polite announcement was wrong for it).
 * Merging the two was proposed and **ruled against** on those structural grounds (2026-08-07,
 * ACK-lib-a-2); it was then **deleted outright** by WS-7 §10.3 when its only call site — `InFlightPane`'s
 * control-plane-logs marker — became a real screen and took its adopter count to zero.
 *
 * ⇒ ⛔ If a page-region "planned" marker is ever wanted again, that is a NEW decision with a real
 * adopter, ⛔ not a `subject`-shaped use of this panel arm.
 */
export function UnimplementedState({
  subject,
  reason,
  remedy,
  trackingIssue,
  testId = 'absence-unimplemented',
}: {
  readonly subject: string;
  /** What is missing, in one sentence. */
  readonly reason: string;
  /** ⛔ The concrete next step. Never empty. */
  readonly remedy: string;
  /** ⛔ Where the gap is tracked, so the answer has an expiry date. */
  readonly trackingIssue: string;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <Frame
      kind="unimplemented"
      testId={testId}
      tone="border-state-info/40 bg-state-info/5"
      icon={<Unplug aria-hidden="true" className="h-3.5 w-3.5 text-state-info" />}
      /* ⛔ "not supported here", never "not collected" — the latter is the claim we cannot make. */
      title={`${subject} is not supported for this data plane yet`}
    >
      <span className="flex flex-col gap-1">
        <span>{reason}</span>
        <span>{remedy}</span>
        <span className="text-muted">
          Tracked in <TrackingIssue value={trackingIssue} testId={testId} />.
        </span>
      </span>
    </Frame>
  );
}

/** `#1269` → `1269`; anything else → `null`. Anchored, so it matches the WHOLE string or nothing. */
function issueNumber(trackingIssue: string): string | null {
  // One to nine digits, the same bound as `trackingIssueHref` in the WARD absence set.
  return /^#(\d{1,9})$/.exec(trackingIssue)?.[1] ?? null;
}

/**
 * The tracking issue, as a LINK when it is recognisably an issue number and as plain text otherwise.
 *
 * ## ⭐ Why a link at all
 *
 * `trackingIssue` exists *"so the answer has an expiry date"* — and an issue number you have to
 * retype into a browser is strictly worse than one you can click. The since-deleted page-region twin
 * (`panes/PlannedSurface.tsx`) always linked it; this arm rendered it as bare text, which was the
 * asymmetry (ruled 2026-08-07). ⚠️ Its deletion (WS-7 §10.3) does NOT retire the argument — the link
 * is right on its own merits, and this is now the only arm that carries one.
 *
 * ## ⛔⛔ WHY IT IS PARSED AND NOT INTERPOLATED — `trackingIssue` IS SERVER-SUPPLIED
 *
 * ⚠️ This is **not** a caller-authored constant. It arrives over the wire:
 * `DpMetricUnavailable.trackingIssue` (`api/dpObservability.api.ts`) → `dp-metrics-model.ts` →
 * `DataPlaneMetricsScreen`. So the obvious implementation — `href={`…/issues/${trackingIssue}`}` — puts
 * **untrusted remote input into an `href`**, which is a `javascript:`/redirect sink, and CodeQL's
 * `js/path-injection` family is the same root cause one layer over.
 *
 * ⇒ **Validate, then CONSTRUCT — never interpolate.** {@link issueNumber} whitelists `^#(\d+)$`
 * (anchored: it matches the entire string or nothing, so `#1 javascript:alert(1)` is not an issue
 * number) and the URL is then built from the extracted DIGITS. Nothing the server sends can reach the
 * `href` except a decimal integer.
 *
 * ⛔ **The non-matching case renders the value as TEXT, never as a dead link and never dropped.** A
 * tracking reference we cannot linkify (`PLAT-42`, a URL, prose) is still the only pointer the reader
 * has to who owns the gap — discarding it would turn "tracked, here" into "unavailable, good luck",
 * which is the thing this component's docblock forbids.
 */
function TrackingIssue({
  value,
  testId,
}: {
  readonly value: string;
  readonly testId: string;
}): React.JSX.Element {
  const number = issueNumber(value);
  if (number === null) return <span data-testid={`${testId}-issue-text`}>{value}</span>;
  return (
    <a
      className="underline underline-offset-2 hover:text-primary"
      data-testid={`${testId}-issue`}
      href={`https://github.com/witness-ai/zion-infra/issues/${number}`}
      /* An external link that can script the opener is a tabnabbing hole. */
      rel="noreferrer"
      target="_blank"
    >
      {value}
    </a>
  );
}

/** The type rung class of each {@link CellAbsence} size. Literal, so Tailwind sees both classes. */
const CELL_ABSENCE_SIZE = { xs: 'text-xs', '2xs': 'text-2xs' } as const;

/**
 * **THE IN-CELL ABSENCE.** The same five kinds, at table-cell scale.
 *
 * ## ⛔ WHY A `<td>` CANNOT HOLD {@link Frame}
 *
 * `Frame` is a PANEL — `rounded-lg border px-4 py-3`, an icon, a bold title, a full sentence of
 * prose, and `role="status" aria-live="polite"`. Five cell components rendered it **once per table
 * row**, and on a 7- or 9-column triage table that is two distinct defects:
 *
 *  1. **Layout blow-out.** `table-layout: auto` sizes a column to its content, so a ~150-character
 *     paragraph in one cell dominates the width distribution: the other columns are squeezed, the
 *     table overflows the main column, and row height goes from ~30 px to ~70 px. The screen whose
 *     whole job is *"led by what is broken"* stops being scannable at exactly the moment something
 *     is broken.
 *  2. **N duplicate live regions.** Up to nine (with two failed reads, up to thirty-six)
 *     `aria-live="polite"` regions mounting in a single commit, so a screen-reader user hears the
 *     same sentence up to nine times before reaching the first row. ⛔ A live region inside a
 *     repeated row template is an anti-pattern at any count.
 *
 * ## What this renders instead, and why each part is load-bearing
 *
 * · ⛔ **`data-absence` STAYS.** It is the machine-checkable half of the #879 contract and
 *   `absence.test.tsx` pins the five values pairwise distinct. A compact rendering that dropped it
 *   would collapse the five back into one blank in every dimension a test can see.
 * · **The visible text is a SHORT label**, not the reason — that is what keeps the column narrow.
 * · ⛔ **The full reason stays IN the cell, as `sr-only` text.** `sr-only` is
 *   `position:absolute` so it is out of flow and contributes nothing to column width, while a
 *   screen-reader user reading the cell still gets every word. ⚠️ Deliberately NOT
 *   `aria-describedby` to a shared per-column note: two of the call sites carry a reason that is
 *   computed PER ROW, and one shared sentence cannot state a per-row fact.
 * · The `title` is for a mouse user, and it is ⛔ never the only channel — that is the H3 defect
 *   this package already documents on `FleetCapacityPane`'s levers.
 * · ⛔ **No `role="status"`, no `aria-live`.** See defect 2.
 *
 * ⚠️ Tone is `text-secondary` for all five kinds rather than the panel's warning/critical colours.
 * At `text-xs` in light mode `text-state-warning` measures 3.04:1 and `text-state-success` 3.15:1 —
 * below AA for normal text — and the kind is already stated in words by `label` and in the DOM by
 * `data-absence`, so colour was never carrying it.
 */
export function CellAbsence({
  kind,
  label,
  reason,
  testId,
  size = 'xs',
}: {
  readonly kind: AbsenceKind;
  /** The SHORT visible form — e.g. `not stated`, `not collected`. Keep it to a few words. */
  readonly label: string;
  /** The full sentence. Reachable by AT and by hover; never rendered into the column's width. */
  readonly reason: string;
  readonly testId: string;
  /**
   * The type rung. `xs` (the default) is the table-cell rung. `2xs` is the caption rung of a
   * `MetaList` value in `IdentityLine.tsx`, so an absence in a pair list reads at the size of the
   * values beside it (the app Overview's Source row, R-009 of plan 2026-10-07-app-source-record).
   */
  readonly size?: 'xs' | '2xs';
}): React.JSX.Element {
  return (
    <span
      data-absence={kind}
      data-testid={testId}
      title={reason}
      className={`inline-flex items-baseline gap-1 ${CELL_ABSENCE_SIZE[size]} text-secondary`}
    >
      {/* The dash is decoration — `label` says the same thing in words, so announcing it twice
          would just add noise. */}
      <span aria-hidden="true">—</span>
      {label}
      <span className="sr-only">{reason}</span>
    </span>
  );
}

/**
 * **`forbidden`** — the `403` rendering. Asking is not yours to do.
 *
 * WS-F §6 assertions 2 and 3: by default this replaces one panel, and `extent="page"` replaces the
 * whole page body (see {@link ForbiddenExtent}). It exists as its own component so that a
 * denial reads as "not yours" rather than "broken" — an operator who sees a red failure box where a permission boundary actually lies goes and files a bug.
 *
 * ## ⛔ IT IS ITS OWN KIND. It used to claim `errorstate`, and that was the defect.
 *
 * ⚠️ Until 2026-08-07 this passed `kind="errorstate"` — see {@link ABSENCE_KINDS} for the full
 * history. The visible prose was always right; the machine-checkable half said *"the query failed"*.
 * ⛔ **The tone is deliberately NOT the critical tone** (`border-border-subtle bg-surface-muted`, a
 * muted `CircleSlash`) and that has always been true — so before the fifth kind existed, the ONLY
 * thing separating a boundary from a crash in the DOM was a caller-chosen `data-testid`. Two facts
 * that pull in opposite directions, and `absence.test.tsx` asserts them together because a future
 * edit is likely to sacrifice one:
 *
 *  · `data-absence="forbidden"` — not `errorstate`, so a machine can tell a 403 from a 500.
 *  · not the critical tone — so a human does not read a permission boundary as an outage.
 */
export function ForbiddenState({
  subject,
  permission,
  scope = 'this app',
  testId = 'absence-forbidden',
  extent = 'panel',
}: {
  readonly subject: string;
  /** The exact grant string required — see §2.1: the resource leg is exact-match, no wildcards. */
  readonly permission: string;
  /**
   * ⭐ **WHAT THE GRANT IS NEEDED *ON*. Defaults to `'this app'` — what the sentence said
   * unconditionally before, and what every existing call site means.**
   *
   * ⛔ Added rather than forked (#2568). Every shipped call site is app-scoped, so the sentence
   * *"requires `X` on this app"* was hard-coded — and the first non-app caller (`TenantPane`'s
   * availability card, on a TENANT screen) would otherwise have had to hand-roll its own panel
   * beside this one. That is the decay this module's header forbids: a vocabulary that refuses a
   * legitimate variant gets copied, and the copy drifts.
   *
   * ⚠️ Not cosmetic. `cp:billing:read` is SITE-WIDE and structurally un-grantable per resource
   * (its second segment is `billing`, not a member of `GRANT_RESOURCE_TYPES`, so the CP resolver
   * throws rather than denying), so telling a reader they need it *"on this app"* sends them to
   * ask for a grant nobody can mint. ⇒ pass `'the control plane'` for a site-wide permission.
   *
   * ⛔ A NOUN PHRASE, not a sentence — it is interpolated after *"on "*.
   */
  readonly scope?: string;
  readonly testId?: string;
  /** How much of the screen the notice replaces. Default `panel`. See {@link ForbiddenExtent}. */
  readonly extent?: ForbiddenExtent;
}): React.JSX.Element {
  return (
    <Frame
      kind="forbidden"
      testId={testId}
      tone="border-border-subtle bg-surface-muted"
      icon={<CircleSlash aria-hidden="true" className="h-3.5 w-3.5 text-muted" />}
      title={`${subject} needs a permission you don't hold`}
    >
      <span>
        This {extent} requires{' '}
        <code className="rounded border border-border-subtle px-1 font-mono text-2xs">
          {permission}
        </code>{' '}
        on {scope}.{extent === 'panel' && ' The rest of this screen is unaffected.'}
      </span>
    </Frame>
  );
}
